import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LineReveal } from "../lib/motion";

const TopoLines = () => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.13]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1440 900" fill="none">
    {Array.from({ length: 9 }).map((_, i) => (
      <path
        key={i}
        d={`M-100 ${180 + i * 80} C 300 ${120 + i * 80}, 600 ${260 + i * 80}, 900 ${180 + i * 80} S 1400 ${120 + i * 80}, 1600 ${200 + i * 80}`}
        stroke="#c9a24a"
        strokeWidth="1"
        fill="none"
      />
    ))}
  </svg>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCrane = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[100svh] bg-navy-deep grain topo overflow-hidden flex items-center"
    >
      {/* layered navy depth */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(20,50,95,0.9),transparent_55%),radial-gradient(circle_at_90%_90%,rgba(10,27,53,0.9),transparent_50%)]" />
      <TopoLines />

      {/* floating crane, parallax */}
      <motion.img
        src="/crane_light.png"
        alt=""
        style={{ y: yCrane, opacity }}
        className="pointer-events-none absolute right-[-4%] top-[12%] w-[46vw] max-w-[620px] opacity-90 animate-floaty drop-shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
      />
      <div className="absolute right-[8%] top-[26%] w-[30vw] max-w-[380px] h-[30vw] max-h-[380px] rounded-full bg-gold/10 blur-[90px]" />

      <motion.div style={{ y: yText }} className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-28 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-gold text-xs md:text-sm uppercase tracking-luxe mb-6 flex items-center gap-3"
        >
          <span className="inline-block h-px w-10 bg-gold" />
          Social Media Marketing, LLC · Est. 2024
        </motion.p>

        <h1 className="font-display font-semibold uppercase text-cream leading-[0.92] text-[15vw] sm:text-[12vw] md:text-[8.5vw] lg:text-[7.2rem] tracking-tight">
          <LineReveal autoPlay delay={0.5} lines={["Let's advance"]} className="block" />
          <LineReveal autoPlay delay={0.7} lines={["your"]} className="block" />
          <LineReveal autoPlay delay={0.9} lines={[<span key="s" className="gold-gradient-text animate-shimmer">story.</span>]} className="block" />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 max-w-xl"
        >
          <p className="text-cream/75 text-base md:text-lg font-light leading-relaxed">
            Bold, purpose-driven social media for civic leaders, celebrations,
            restaurants, and the brands ready to rise. We help you show up well,
            tell your story, and advance the work you're called to do.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => document.querySelector("#promo").scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center justify-center gap-2 bg-gold text-navy-deep font-semibold uppercase tracking-[0.15em] text-xs px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
            >
              Claim the $50 Offer
              <ArrowDown className="transition-transform duration-300 group-hover:translate-y-0.5" size={16} />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.querySelector("#chapters").scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2 border border-cream/40 text-cream font-medium uppercase tracking-[0.15em] text-xs px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Who We Serve
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/50 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-luxe">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
